import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  Paper,
  Divider,
  Fade,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CelebrationIcon from "@mui/icons-material/Celebration";
import HomeIcon from "@mui/icons-material/Home";
import Confetti from "react-confetti";
import { Link, useSearchParams } from "react-router-dom";

const PaymentSuccess = ({ styles, theme }) => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Get the session ID and user ID from URL
    const sessionId = searchParams.get("session_id");
    const userId = searchParams.get("user_id");

    if (!sessionId || !userId) {
      setError("Missing payment information");
      setLoading(false);
      return;
    }

    // Verify payment and update user
    const verifyPayment = async () => {
      try {
        const response = await fetch(
          `${process.env.NODE_ENV === "production" ? "/api" : "http://localhost:3001/api"}/verify-payment?session_id=${sessionId}&user_id=${userId}`,
        );
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || "Payment verification failed");
        }
        setLoading(false);
      } catch (err) {
        console.error("Payment verification error:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    verifyPayment();
  }, [searchParams]);

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ textAlign: "center", py: 10 }}>
        <Paper
          elevation={3}
          sx={{
            ...styles.freshBox,
            ...styles.softBoxShadow,
            py: 6,
            px: 4,
            borderRadius: 4,
          }}
        >
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 3, fontWeight: 500 }}>
            Verifierar din betalning...
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
            Det tar bara ett ögonblick att bekräfta din transaktion
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ textAlign: "center", py: 10 }}>
        <Paper
          elevation={3}
          sx={{
            ...styles.freshBox,
            ...styles.softBoxShadow,
            py: 6,
            px: 4,
            borderRadius: 4,
          }}
        >
          <Typography variant="h5" color="error" gutterBottom>
            {error}
          </Typography>
          <Typography
            variant="body2"
            paragraph
            sx={{ mt: 1, mb: 3, color: "text.secondary" }}
          >
            Det uppstod ett problem vid verifiering av din betalning. Vänligen
            försök igen eller kontakta vår kundtjänst.
          </Typography>
          <Button component={Link} to="/app" variant="purp" sx={{ mt: 2 }}>
            Tillbaka till startsidan
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ textAlign: "center", py: 10 }}>
      <Fade in={true} timeout={800}>
        <Paper
          elevation={3}
          sx={{
            ...styles.freshBox,
            ...styles.softBoxShadow,
            py: 6,
            px: 4,
            borderRadius: 4,
            position: "relative",
          }}
        >
          <Confetti
            width="550px"
            height="590px"
            recycle={false}
            numberOfPieces={400}
            gravity={0.3}
          />

          <Box sx={{ mb: 4, display: "flex", justifyContent: "center" }}>
            <Box sx={{ position: "relative" }}>
              <CheckCircleIcon sx={{ fontSize: 90, color: "rgb(77 178 82)" }} />
            </Box>
          </Box>

          <Typography variant="h4" gutterBottom fontWeight={600}>
            Tack för ditt köp!
          </Typography>

          <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
            Din betalning har genomförts framgångsrikt
          </Typography>

          <Divider sx={{ my: 3, display: "none" }} />

          <Box sx={{ my: 3, px: 2, display: "none" }}>
            <Typography variant="body1" paragraph sx={{ mb: 3 }}>
              Din prenumeration är nu aktiv. Du kan när som helst avbryta din
              prenumeration genom att gå in på <strong>Inställningar</strong>{" "}
              och välja Prenumeration".
            </Typography>
          </Box>

          <Button
            variant="purp"
            size="large"
            startIcon={<HomeIcon />}
            sx={{ mt: 2 }}
            onClick={() => (window.location.href = "/app")}
          >
            Gå till startsidan
          </Button>
          <Divider sx={{ my: 5 }} />
          <Typography variant="body2" sx={{ mt: 4, color: "text.secondary" }}>
            En bekräftelse har skickats till din e-post. Du kan när som helst
            avsluta prenumerationen via <strong>Inställningar</strong> »{" "}
            <strong>Prenumeration</strong>.
          </Typography>
        </Paper>
      </Fade>
    </Container>
  );
};

export default PaymentSuccess;
